import React, { useEffect, useMemo, useState } from 'react'
import { useActivityStore } from '../Contexts/ActivityContext'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useUtilsStore } from '../Contexts/UtilsContext'
import { useViewStore } from '../Contexts/ViewContext'

import type {
  RetorikActivity,
  HistoryActivity
} from '../../models/activityTypes'
import { checkListDisplay } from '../../utils/checkListDisplay'
import { createNewQuestionForTimestamp } from '../../utils/activityUtils'
import { ListStatus } from '../../models/enums'

import ListTemplate from '../Templates/List'
import ConversationPanelMobile from './ConversationPanel/ConversationPanelMobile'
import ConversationPanelLarge from './ConversationPanel/ConversationPanelLarge'

const ConversationPanel = (): JSX.Element => {
  const messageActivities = useActivityStore((state) => state.messageActivities)
  const streamingActivities = useActivityStore(
    (state) => state.botStreamingActivities
  )
  const forceMobileView = useRetorikStore(
    (state) => state.configuration.forceMobileView
  )
  const isMobile = useViewStore((state) => state.isMobile)
  const listStatus = useUtilsStore((state) => state.listStatus)
  const setListStatus = useUtilsStore((state) => state.actions.setListStatus)
  const [listActivity, setListActivity] = useState<RetorikActivity | undefined>(
    undefined
  )
  const [itemsInConversation, setItemsInConversation] = useState<number>(0)

  const getActivitiesTextMode = (
    activities: Array<RetorikActivity>,
    streamingActivities: Map<string, Array<RetorikActivity>>
  ): Array<HistoryActivity> => {
    // processedId contains the ids of the activities already processed (because useActivities gives duplicate activities with different state depending on if it's been spoken or not )
    const processedId: Array<string> = []
    const history: HistoryActivity[] = []

    let tempItemsInConversation = 0
    activities.forEach((activity) => {
      if (activity.id && !processedId.includes(activity.id)) {
        processedId.push(activity.id)
        // Check if this is a question or an answer (questions from the user don't have a replyToId field)
        if (activity.replyToId) {
          const question: HistoryActivity | undefined = history.find(
            (historyItem) => historyItem.question.id === activity.replyToId
          )
          if (question) {
            if (
              !question.answers.length ||
              !question.answers.find((item) => item.id === activity.id)
            ) {
              question.answers.push(activity)
              tempItemsInConversation++
            }
          } else {
            const newQuestion: RetorikActivity =
              createNewQuestionForTimestamp(activity)

            history.push({
              question: newQuestion,
              answers: [activity]
            })
            tempItemsInConversation++
          }
        } else {
          // If the activity has a value, it is a need for further informations that don't need to be displayed
          if (!(activity as RetorikActivity).value) {
            // Hack for Bringup Message to be displayed as bot message
            if (
              (activity as RetorikActivity).label === 'Davi.BringupMessage' &&
              (activity as RetorikActivity).text
            ) {
              const newQuestion: RetorikActivity =
                createNewQuestionForTimestamp(activity)
              history.push({
                question: newQuestion,
                answers: [activity]
              })

              tempItemsInConversation++
            } else {
              const streamingAnswers = streamingActivities.get(activity.id)
              history.push({
                question: activity,
                answers: streamingAnswers || []
              })

              tempItemsInConversation += streamingAnswers?.length || 0
            }
          }
        }
      }
    })

    setItemsInConversation(tempItemsInConversation)

    return history
  }

  const activities = useMemo<Array<HistoryActivity>>(() => {
    return getActivitiesTextMode(messageActivities, streamingActivities)
  }, [messageActivities, streamingActivities])

  /**
   * Check if there is an activity in need of List display in bot's answers.
   */
  useEffect(() => {
    const answers = activities.length
      ? activities[activities.length - 1].answers
      : []
    setListActivity(
      answers.find((activity: RetorikActivity) => checkListDisplay(activity))
    )
  }, [itemsInConversation])

  useEffect(() => {
    listStatus === ListStatus.closed && setListActivity(undefined)
  }, [listStatus])

  return (
    <React.Fragment>
      {listActivity && !(listStatus === ListStatus.closed) ? (
        <ListTemplate
          activity={listActivity}
          onClose={() => setListStatus(ListStatus.closed)}
        />
      ) : isMobile || forceMobileView ? (
        <ConversationPanelMobile
          activities={activities}
          itemsInConversation={itemsInConversation}
        />
      ) : (
        <ConversationPanelLarge
          activities={activities}
          itemsInConversation={itemsInConversation}
        />
      )}
    </React.Fragment>
  )
}

export default ConversationPanel
